import debug from 'debug';
import { CGProduct } from '../types/shopify';
import { CARD_MEDIUM_DIGITAL, CARD_MEDIUM_PHYSICAL } from 'utilities/constants';
import { isCustomDesignCardByHandle, isPredesignedCardByHandle } from 'components/CardConfigurer/configurerFlags';

const d = debug('libProduct');

export const stripGC = (title: string) => {
  if (title.includes('The') && !title.includes('Good')) return title;
  return title.replace('Gift Card', '').trim();
};
/**
 * Pre-designed and custom cards should always link back to the main product (e.g. from the 'Add another' link in the cart)
 *
 * Here we detect if a handle contains a reference to being a designed or custom card and use the prefix to return the main card handle
 * @param handle
 */
export const getRootCardHandleFromVariantHandle = (handle: string) => {
  if (isCustomDesignCardByHandle(handle) || isPredesignedCardByHandle(handle)) {
    // We just want the very first part of the handle...
    return handle.substring(0, handle.indexOf('-'));
  }
  return handle;
};

export const getCardValueByVariant = (variant: any) => {
  if (variant.compareAtPrice) {
    return variant.compareAtPrice;
  } else {
    return variant.price;
  }
};

export const getCardFeeByVariant = (variant: any) => {
  if (variant && variant.compareAtPrice && variant.compareAtPrice !== variant.price) {
    return parseFloat(variant.price) - parseFloat(variant.compareAtPrice);
  } else {
    return 0;
  }
};

export const productGetSplash = (product: CGProduct): false | string => {
  let foundSplash: any = false;
  product.images.forEach((img) => {
    if (img.src && (img.src.includes('-splash.') || img.src.includes('-unsplash.'))) {
      foundSplash = img.src;
    }
  });
  return foundSplash;
};

export const getCardMediums = (product: CGProduct, raw: boolean = false, strict: boolean = false) => {
  let foundDigital = false;
  let foundPhysical = false;
  getProductAmountOptions(product, strict).forEach((item: any) => {
    const itemValue = strict ? item : item.toLowerCase();
    if (itemValue.includes(CARD_MEDIUM_DIGITAL)) foundDigital = true;
    if (itemValue.includes(CARD_MEDIUM_PHYSICAL)) foundPhysical = true;
  });
  const mediums: any[] = [];
  if (raw) {
    if (foundDigital) mediums.push(CARD_MEDIUM_DIGITAL);
    if (foundPhysical) mediums.push(CARD_MEDIUM_PHYSICAL);
  } else {
    if (foundDigital) mediums.push({ label: 'Digital', value: CARD_MEDIUM_DIGITAL });
    if (foundPhysical) mediums.push({ label: 'Physical', value: CARD_MEDIUM_PHYSICAL });
  }
  if (!mediums.length) {
    console.warn('cardHasNoMediums', product.handle);
    d('cardHasNoMediums!!!', product.handle, product);
  }
  return mediums;
};

export const getCardValues = (product: CGProduct, medium: string = '', raw: boolean = false) => {
  const values: any[] = [];
  getProductAmountOptions(product).forEach((item: any) => {
    const amountParts = item.split('-');
    const amount = parseFloat(amountParts[0]);
    if (isNaN(amount)) {
      console.warn('badCardValueOption', product.handle, item);
    } else {
      if (medium && medium.length) {
        if (item.includes(medium) && values.indexOf(amount) === -1) values.push(amount);
      } else {
        if (values.indexOf(amount) === -1) values.push(amount);
      }
    }
    // d(amountParts);
  });
  return values
    .sort((a, b) => {
      return a - b;
    })
    .map((value) => {
      if (raw) return value;
      return {
        label: '$' + value,
        value: value,
      };
    });
};

export const getCardMinMaxValues = (product: CGProduct, medium: string = ''): number[] => {
  const values = getCardValues(product, medium, true);
  if (values.length >= 2) return [values[0], values[values.length - 1]];
  return values;
};

export const getProductAmountOptions = (product: CGProduct, strict: boolean = false) => {
  if (!product.options) {
    console.warn('noProductOptions', product.handle, product);
  }
  const amount = (product.options || []).filter((option) => option.name === 'Amount');
  if (amount.length) {
    const actualAmountOptions = amount[0].values;
    return actualAmountOptions.map((val) => {
      const optionValue = val.hasOwnProperty('value') ? val.value : val;
      return strict ? optionValue : optionValue.toLowerCase();
    });
  } else {
    return [];
  }
};

export const getCardVariantIdByMediumAndValue = (product: CGProduct, medium: string, value: number) => {
  if (!value) {
    console.warn(`Variant ID not found for product: "${product.handle}", medium: "${medium}", value: "${value}"`);
    // TODO - Build product validation check before showing Configurer
    return undefined;
  }

  const amountOptions = product.variants.map((variant) => {
    const variantId = variant.id;
    const selectedOptions = (variant as any).selectedOptions || [];
    const amountOption = selectedOptions?.find((option: any) => option.name === 'Amount');
    return { variantId, amount: amountOption?.value.toLowerCase() };
  });

  const cardVariant = amountOptions.find((option) => option.amount === value.toString() + '-' + medium);
  const variantId = cardVariant?.variantId;

  if (!variantId) {
    console.error(`Variant ID not found for product: "${product.handle}", medium: "${medium}", value: "${value}"`);
  }

  return variantId;
};
